import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Alert,
} from '@mui/material';
import { useTenantStore } from '../../../stores/tenant.store';
import { useAcademic } from '../../../hooks/use-academic';
import { ManageDocumentRequirements } from './ManageDocumentRequirements';

/** Minimal shape the selector needs from a department. */
export interface ProgramDepartmentOption {
  id: string;
  name: string;
  code?: string;
}

interface CreateProgramDialogProps {
  open: boolean;
  onClose: () => void;
  departments: ProgramDepartmentOption[];
}

export function CreateProgramDialog({ open, onClose, departments }: CreateProgramDialogProps) {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const { createProgram, isCreatingProgram } = useAcademic();

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [durationTerms, setDurationTerms] = useState<number | ''>('');
  const [documentRequirements, setDocumentRequirements] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  const noDepartments = departments.length === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentTenantId) return;

    setError(null);
    try {
      await createProgram({
        tenantId: currentTenantId,
        name,
        code,
        departmentId,
        durationTerms: Number(durationTerms) || 0,
        documentRequirements,
      });
      onClose();
      setName('');
      setCode('');
      setDepartmentId('');
      setDurationTerms('');
      setDocumentRequirements([]);
    } catch (err: any) {
      setError(err.message || 'Error creating program');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('programDialogs.create.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {noDepartments && (
              <Alert severity="warning">{t('programDialogs.create.fields.departmentEmpty')}</Alert>
            )}

            <TextField
              label={t('programDialogs.create.fields.name')}
              required
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              helperText={t('programDialogs.create.fields.nameHelper')}
            />

            <TextField
              select
              label={t('programDialogs.create.fields.department')}
              required
              fullWidth
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              disabled={noDepartments}
              helperText={t('programDialogs.create.fields.departmentHelper')}
            >
              {departments.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.code ? `${d.name} (${d.code})` : d.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label={t('programDialogs.create.fields.code')}
              required
              fullWidth
              value={code}
              onChange={(e) => setCode(e.target.value)}
              helperText={t('programDialogs.create.fields.codeHelper')}
            />

            <TextField
              label={t('programDialogs.create.fields.duration')}
              type="number"
              required
              fullWidth
              value={durationTerms}
              onChange={(e) => setDurationTerms(e.target.value ? Number(e.target.value) : '')}
              helperText={t('programDialogs.create.fields.durationHelper')}
            />

            <ManageDocumentRequirements
              requirements={documentRequirements}
              onChange={setDocumentRequirements}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={isCreatingProgram}>
            {t('programDialogs.create.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isCreatingProgram || !departmentId}
            sx={{ minWidth: 120 }}
          >
            {isCreatingProgram
              ? t('programDialogs.create.actions.creating')
              : t('programDialogs.create.actions.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
